import { Archive, ArchiveRestore, ArrowLeft, Mail, MoreHorizontal, Pencil } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ArtistAvatar } from '@/components/artist-avatar';
import { ArtistDialog, type ArtistValues } from '@/components/artist-dialog';
import { clearComboboxCache } from '@/components/combobox';
import { ResponsiveTable } from '@/components/responsive-table';
import { RichText } from '@/components/rich-text';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useArtist, useProposals } from '@/lib/api-hooks';
import { archiveArtist, restoreArtist, updateArtist } from '@/lib/artists';
import { proposalStatusStyles } from '@/lib/proposal-status';
import type { Proposal } from '@/lib/proposals';
import { invalidateResource } from '@/lib/resource';
import { cn } from '@/lib/utils';

export function ArtistDetailPage() {
  const { slug } = useParams();
  const { data: artist, isLoading } = useArtist(slug);
  const { data: proposalsData } = useProposals({ artistSlug: slug, pageSize: 100 });
  const proposals = proposalsData?.items ?? [];
  const [editOpen, setEditOpen] = useState(false);

  async function handleEdit(values: ArtistValues) {
    if (!artist) return;
    await updateArtist(artist.id, values);
    clearComboboxCache('artists');
    invalidateResource('artist');
    invalidateResource('artists');
  }

  async function handleArchive() {
    if (!artist) return;
    await archiveArtist(artist.id);
    clearComboboxCache('artists');
    invalidateResource('artist');
  }

  async function handleRestore() {
    if (!artist) return;
    await restoreArtist(artist.id);
    clearComboboxCache('artists');
    invalidateResource('artist');
  }

  if (isLoading) {
    return <p className="text-muted-foreground text-sm">Loading artist…</p>;
  }

  if (!artist) {
    return (
      <div className="flex flex-col items-start gap-4">
        <Link
          to="/artists"
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to artists
        </Link>
        <p className="text-muted-foreground text-sm">Artist not found.</p>
      </div>
    );
  }

  const stats = [
    { label: 'Projects', value: String(artist.projects) },
    { label: 'Proposals', value: String(artist.proposals) },
    { label: 'Votes', value: artist.votes.toLocaleString() },
    { label: 'Updated', value: artist.updated },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <Link
          to="/artists"
          className="inline-flex w-fit items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to artists
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="size-4" />
            Edit
          </Button>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger
              aria-label="Artist actions"
              className="inline-flex size-9 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <MoreHorizontal className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {artist.archived ? (
                <DropdownMenuItem onSelect={handleRestore}>
                  <ArchiveRestore />
                  Restore
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={handleArchive}>
                  <Archive />
                  Archive
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex items-start gap-4">
        <ArtistAvatar
          name={artist.name}
          src={artist.avatarUrl}
          className="size-14 font-semibold text-lg"
        />
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-semibold text-2xl tracking-tight">{artist.name}</h1>
            <Badge variant="outline">{artist.discipline}</Badge>
          </div>
          <RichText html={artist.bio} className="max-w-2xl text-muted-foreground" />
          {artist.email && (
            <a
              href={`mailto:${artist.email}`}
              className="inline-flex items-center gap-1.5 text-muted-foreground text-sm hover:text-foreground"
            >
              <Mail className="size-3.5" />
              {artist.email}
            </a>
          )}
        </div>
      </div>

      <div className="grid gap-4 border-y py-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="space-y-1">
            <p className="text-muted-foreground text-sm">{stat.label}</p>
            <p className="font-medium text-base">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3">
        <div className="space-y-1">
          <h2 className="font-semibold text-lg tracking-tight">Proposals</h2>
          <p className="text-muted-foreground text-sm">Proposals by this artist.</p>
        </div>
        <ResponsiveTable
          rows={proposals}
          rowKey={(proposal) => proposal.id}
          emptyLabel="No proposals yet."
          columns={[
            {
              key: 'proposal',
              header: 'Proposal',
              title: true,
              cell: (proposal: Proposal) => (
                <Link to={`/proposals/${proposal.slug}`} className="font-medium hover:underline">
                  {proposal.title}
                </Link>
              ),
            },
            {
              key: 'project',
              header: 'Project',
              cell: (proposal: Proposal) => (
                <Link
                  to={`/projects/${proposal.projectSlug}`}
                  className="text-muted-foreground hover:text-foreground hover:underline"
                >
                  {proposal.project}
                </Link>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              cell: (proposal: Proposal) => (
                <Badge variant="outline" className={cn(proposalStatusStyles[proposal.status])}>
                  {proposal.status}
                </Badge>
              ),
            },
            {
              key: 'votes',
              header: 'Votes',
              align: 'right',
              cell: (proposal: Proposal) =>
                proposal.votes > 0 ? proposal.votes.toLocaleString() : '—',
            },
          ]}
        />
      </div>

      {editOpen && (
        <ArtistDialog
          open
          onOpenChange={setEditOpen}
          mode="edit"
          initialValues={{
            name: artist.name,
            discipline: artist.discipline,
            bio: artist.bio,
            email: artist.email,
            avatarMediaId: artist.avatarMediaId ?? null,
            avatarUrl: artist.avatarUrl ?? null,
          }}
          onSubmit={handleEdit}
        />
      )}
    </div>
  );
}
